import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const PAGE_SIZE = 12;

export interface ListingWithProfile {
  id: string;
  user_id: string;
  title: string;
  description: string;
  category: string;
  price: number | null;
  currency: string;
  location: string | null;
  image_urls: string[];
  video_url: string | null;
  is_auction: boolean;
  auction_end_at: string | null;
  min_bid: number | null;
  buy_now_price: number | null;
  status: string;
  views_count: number;
  bids_count: number;
  created_at: string;
  updated_at: string;
  profiles: {
    display_name: string | null;
    avatar_url: string | null;
    username: string | null;
    is_verified: boolean | null;
  } | null;
}

export interface Bid {
  id: string;
  listing_id: string;
  user_id: string;
  amount: number;
  message: string | null;
  status: string;
  created_at: string;
  profiles?: {
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

export const useListings = (category?: string, search?: string) => {
  return useInfiniteQuery({
    queryKey: ["listings", category, search],
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase
        .from("listings")
        .select("*")
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .range(pageParam, pageParam + PAGE_SIZE - 1);

      if (category && category !== "all") {
        query = query.eq("category", category as any);
      }
      if (search && search.trim()) {
        query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
      }

      const { data: listings, error } = await query;
      if (error) throw error;
      if (!listings || listings.length === 0) return [] as ListingWithProfile[];

      const userIds = [...new Set(listings.map((l) => l.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url, username, is_verified")
        .in("user_id", userIds);
      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) ?? []);

      return listings.map((l) => ({
        ...l,
        image_urls: l.image_urls ?? [],
        profiles: profileMap.get(l.user_id) ?? null,
      })) as ListingWithProfile[];
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    initialPageParam: 0,
  });
};

export const useListing = (id: string | null) => {
  return useQuery({
    queryKey: ["listing", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;

      const { data: profile } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url, username, is_verified")
        .eq("user_id", data.user_id)
        .single();

      return { ...data, image_urls: data.image_urls ?? [], profiles: profile } as ListingWithProfile;
    },
    enabled: !!id,
  });
};

export const useListingBids = (listingId: string | null) => {
  return useQuery({
    queryKey: ["bids", listingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bids")
        .select("*")
        .eq("listing_id", listingId!)
        .order("amount", { ascending: false });
      if (error) throw error;
      if (!data || data.length === 0) return [] as Bid[];

      const userIds = [...new Set(data.map((b) => b.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", userIds);
      const profileMap = new Map(profiles?.map((p) => [p.user_id, p]) ?? []);

      return data.map((b) => ({ ...b, profiles: profileMap.get(b.user_id) ?? null })) as Bid[];
    },
    enabled: !!listingId,
  });
};

export const useCreateListing = () => {
  const queryClient = useQueryClient();
  const { user, plan } = useAuth();
  return useMutation({
    mutationFn: async (listing: {
      title: string;
      description: string;
      category: string;
      price?: number;
      currency?: string;
      location?: string;
      image_urls?: string[];
      is_auction?: boolean;
      min_bid?: number;
      buy_now_price?: number;
      auction_end_at?: string;
      reserve_price?: number;
      anti_snipe_minutes?: number;
    }) => {
      // Free plan listings expire after 30 days
      const expiresAt = plan === "free"
        ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        : null;

      const { error } = await supabase.from("listings").insert({
        title: listing.title,
        description: listing.description,
        category: listing.category as any,
        price: listing.price,
        currency: listing.currency,
        location: listing.location,
        image_urls: listing.image_urls,
        is_auction: listing.is_auction,
        min_bid: listing.min_bid,
        buy_now_price: listing.buy_now_price,
        auction_end_at: listing.auction_end_at,
        reserve_price: listing.reserve_price,
        anti_snipe_minutes: listing.anti_snipe_minutes,
        user_id: user!.id,
        expires_at: expiresAt,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["listings"] }),
  });
};

export const usePlaceBid = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({
      listingId,
      amount,
      message,
      maxProxy,
    }: {
      listingId: string;
      amount: number;
      message?: string;
      maxProxy?: number;
    }) => {
      if (!user) throw new Error("Sign in to place a bid");
      const { data, error } = await supabase.rpc("place_auction_bid", {
        p_listing_id: listingId,
        p_amount: amount,
        p_max_proxy: maxProxy ?? null,
        p_message: message ?? null,
      });
      if (error) throw error;
      return data as {
        top_amount: number;
        extended: boolean;
        auction_end_at: string | null;
        reserve_met: boolean;
      };
    },
    onSuccess: (_, { listingId }) => {
      queryClient.invalidateQueries({ queryKey: ["bids", listingId] });
      queryClient.invalidateQueries({ queryKey: ["listings"] });
      queryClient.invalidateQueries({ queryKey: ["listing", listingId] });
    },
  });
};

export const useUserRole = () => {
  const { user, loading } = useAuth();
  return useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) throw error;
      return data?.map((r) => r.role) ?? [];
    },
    enabled: !!user?.id && !loading,
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
    refetchOnMount: "always",
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
};
